import AddRoadIcon from '@mui/icons-material/AddRoad'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import { Box, Card, CardActionArea, CardContent, Grid, Typography } from '@mui/material'
import { PieChart } from '@mui/x-charts/PieChart'
import { useQueryClient } from '@tanstack/react-query'
import { useEntitiesFromSearch } from 'api/search'
import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useFilteredTasks } from '../../api/aws/tasks/useFilteredTasks'
import { useLayoutContext } from '../../context/LayoutProvider/LayoutProvider'
import { useChangeView } from '../../utils/useChangeView'
import { renderColorByString } from '../DataGrid/renderColorByString'
import { handleFormatTypesString } from '../Forms/types'
import DetailsLoading from '../Loading/DetailsLoading'

const extractStatusFromId = id => {
  const parts = id.split('-')
  const last = parts[parts.length - 1]

  // Check if the last part is a number (your index)
  if (!isNaN(last)) {
    return parts.slice(0, -1).join('-') // Join all parts except the index
  }

  // Fallback if the last part is not a number (rare edge case)
  return id
}

const Boards = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { pinned } = useLayoutContext()
  const handleChangeView = useChangeView()
  const filters = {
    status: ['Completed', 'Completed Internally'],
    stage: 'Deal Won',
    type: 'MSA',
    // productCategories: ['Digital'],
    // productSubcategories: [
    //   'Videography',
    //   'Graphic Design', // Filters deals with Search category in products
    // ],
    excludedDealStatus: ['Expired', 'Expired (system)', 'Cancelled'],
  }
  const projectsFilters = {
    status: ['Completed', 'Completed Internally'],
    stage: 'Deal Won',
    type: ['CSA', 'MSA', 'MSA - Housing'],
    productCategories: ['Creative', 'Website'],
    productSubcategories: [
      'Videography',
      'Graphic Design',
      'Photography',
      'People',
      'Graphic Design',
      'Equipment',
      'Discovery',
      'Creative',
    ],
    excludedDealStatus: ['Expired', 'Expired (system)', 'Cancelled'],
  }
  const orgFilters = {
    status: [
      'Active',
      'Active (system)',
      'In Progress',
      'Month To Month',
      '30 Day Notice',
      'Active (system)',
      'Active (SEO)',
      'Active (Creative)',
      'Active (Website)',
      'Active (LTP)',
    ],
  }
  const orgStatusFilters = orgFilters?.status
  const { deals, organizations } = useEntitiesFromSearch()
  const filteredDeals = deals?.filter(
    deal =>
      ['Completed', 'Completed Internally']?.includes(deal?.status) &&
      deal?.stage === 'Deal Won' &&
      deal?.type === 'MSA'
  )
  const projectDeals = deals?.filter(
    deal =>
      ['Completed', 'Completed Internally']?.includes(deal?.status) &&
      deal?.stage === 'Deal Won' &&
      ['CSA', 'MSA', 'MSA - Housing']?.includes(deal?.type)
  )
  const { filteredTasks: teamTasks, isFilteredTasksLoading } = useFilteredTasks(true, 'teamTasks', {}) // ✅ Pass empty filters to get all
  const filteredOrganizations = organizations?.filter(o => orgStatusFilters?.includes(o?.status))
  const projects = queryClient.getQueryData([
    ['projectsPage', 'deals'],
    {
      status: ['Completed', 'Completed Internally'],
      stage: 'Deal Won',
      type: ['CSA', 'MSA', 'MSA - Housing'],
      productCategories: ['Creative', 'Website'],
      productSubcategories: [
        'Videography',
        'Graphic Design',
        'Photography',
        'People',
        'Graphic Design',
        'Equipment',
        'Discovery',
        'Creative',
      ],
      excludedDealStatus: ['Expired', 'Expired (system)', 'Cancelled'],
    },
  ])
  // 🔹 Aggregate `ncoStatus` counts efficiently
  const ncoStatusCounts = useMemo(() => {
    if (!filteredDeals) return {} // Ensure deals is defined
    return filteredDeals.reduce((acc, deal) => {
      if (deal.onboardingStatus) {
        acc[deal.onboardingStatus] = (acc[deal.onboardingStatus] || 0) + 1
      }
      return acc
    }, {})
  }, [filteredDeals])
  const teamTaskTypeCounts = useMemo(() => {
    if (!teamTasks) return {}

    return teamTasks.reduce((acc, task) => {
      if (task.type) {
        const formattedType = handleFormatTypesString(task.type)
        acc[formattedType] = (acc[formattedType] || 0) + 1
      }
      return acc
    }, {})
  }, [teamTasks])

  const projectsTypeCounts = useMemo(() => {
    if (!projectDeals) return {}
    return projectDeals.reduce((acc, project) => {
      if (project.creativeType?.[0]) {
        acc[project.creativeType?.[0]] = (acc[project.creativeType?.[0]] || 0) + 1
      }
      return acc
    }, {})
  }, [projectDeals])
  const projectStatusCounts = useMemo(() => {
    if (!projectDeals) return {}
    return projectDeals.reduce((acc, project) => {
      if (project?.dealStatus) {
        acc[project?.dealStatus] = (acc[project?.dealStatus] || 0) + 1
      }
      return acc
    }, {})
  }, [projectDeals])
  console.log('projectStatusCounts', projectStatusCounts)
  const teamTaskStatusCounts = useMemo(() => {
    if (!teamTasks) return {} // Ensure deals is defined
    return teamTasks.reduce((acc, task) => {
      if (task.masterStatus) {
        acc[task.masterStatus] = (acc[task.masterStatus] || 0) + 1
      }
      return acc
    }, {})
  }, [teamTasks])
  const mtbStatusCounts = useMemo(() => {
    if (!filteredOrganizations) return {} // Ensure deals is defined
    // filteredOrganizations?.forEach(o => console.log(o?.status))
    return filteredOrganizations.reduce((acc, org) => {
      if (org.status) {
        acc[org.status] = (acc[org.status] || 0) + 1
      }
      return acc
    }, {})
  }, [filteredOrganizations])

  const totalNcoDeals = useMemo(
    () => Object.values(ncoStatusCounts || {}).reduce((sum: number, count: any) => sum + (count as number), 0),
    [ncoStatusCounts]
  )
  const totalProjectDeals = useMemo(() => projectDeals?.length || 0, [projectDeals])
  const totalTaskCount = useMemo(() => teamTasks?.length || 0, [teamTasks])
  const totalMtbCount = useMemo(() => filteredOrganizations?.length || 0, [filteredOrganizations])

  // 🔹 Convert ncoStatusCounts into an array format for MUI Pie Chart
  const pieChartData = useMemo(
    () =>
      Object.entries(ncoStatusCounts || {}).map(([status, count]) => ({
        id: status,
        value: count as number,
        label: status, // Removes labels from chart
        color: renderColorByString(status),
      })),
    [ncoStatusCounts]
  )
  const taskTypePieChartData = useMemo(
    () =>
      Object.entries(teamTaskTypeCounts || {}).map(([type, count], index) => {
        const safeType = type && type.trim() ? type : `No Type - ${index}-${count}`
        return {
          id: safeType,
          value: count as number,
          label: safeType, // Removes labels from chat
          color: renderColorByString(safeType),
        }
      }),
    [teamTaskTypeCounts]
  )
  const projectStatusChartData = useMemo(
    () =>
      Object.entries(projectStatusCounts || {}).map(([status, count], index) => {
        const safeStatus = status && status.trim() ? status : `No Status - ${index}-${count}`
        return {
          id: safeStatus,
          value: count as number,
          label: safeStatus,
          color: renderColorByString(safeStatus),
        }
      }),
    [projectStatusCounts]
  )
  // const mtbStatusChartData = useMemo(() => {
  //   if (!mtbStatusCounts || Object.keys(mtbStatusCounts).length === 0) return []

  //   return Object.entries(mtbStatusCounts).map(([status, count], index) => {
  //     const safeStatus = status?.trim() || 'No Status'
  //     const safeId = `${safeStatus}-${index}`
  //     const safeValue = typeof count === 'number' ? count : 0
  //     const color = renderColorByString(safeStatus) || '#ccc'
  //     return {
  //       id: safeId,
  //       value: safeValue,
  //       label: safeStatus,
  //       color,
  //     }
  //   })
  // }, [mtbStatusCounts])
  const mtbStatusChartData = useMemo(() => {
    if (!mtbStatusCounts || Object.keys(mtbStatusCounts).length === 0) return []

    return Object.entries(mtbStatusCounts).map(([status, count], index) => {
      const safeStatus = status?.trim() || 'No Status'
      const safeId = `${safeStatus}-${index}`
      const safeValue = Number(count) || 0 // ✅ Ensure number
      const color = typeof renderColorByString(safeStatus) === 'string' ? renderColorByString(safeStatus) : '#ccc' // ✅ Ensure string
      return {
        id: safeId,
        value: safeValue,
        label: safeStatus,
        color,
      }
    })
  }, [mtbStatusCounts])

  const boards = [
    {
      title: 'New Client Onboards',
      route: '/nco',
      count: totalNcoDeals,
      icon: <AddRoadIcon sx={{ color: 'rgba(159, 90, 253, 1)' }} />,
    },
    {
      title: 'Projects',
      route: '/projects',
      count: totalProjectDeals, // ✅ Added here
      icon: <RocketLaunchIcon sx={{ color: 'rgb(245, 108, 53)' }} />,
    },
    {
      title: 'Team Tasks',
      route: '/teamtasks',
      count: totalTaskCount,
      icon: <TaskAltIcon sx={{ color: 'rgb(247, 63, 236)' }} />,
    },
    {
      title: 'Monthly Task Board',
      route: '/mtb',
      count: totalMtbCount,
      icon: <CalendarMonthIcon sx={{ color: 'rgb(20, 119, 248)' }} />,
    },
  ]
  const handleNavigate = route => {
    const boardViewMapping = {
      '/nco': 'nco',
      '/projects': 'projects',
      '/deals': 'deals',
      '/organizations': 'organizations',
      '/contacts': 'contacts',
      '/leads': 'leads',
      '/teamtasks': 'teamtasks',
      '/mtb': 'mtb',
    }

    const newView = boardViewMapping[route] || null

    if (newView) {
      handleChangeView({ newView })
    }

    navigate(route)
  }
  useEffect(() => {
    if (projectStatusChartData.length) {
      console.log('projectStatusChartData', projectStatusChartData)
      const allValid = projectStatusChartData.every(d => typeof d.id === 'string' && typeof d.value === 'number')
      if (!allValid) {
        console.warn('❌ Invalid projectStatusChartData structure:', projectStatusChartData)
      }
    }
  }, [projectStatusChartData])
  useEffect(() => {
    const bad = projectStatusChartData.find(
      d =>
        typeof d.id !== 'string' ||
        typeof d.value !== 'number' ||
        typeof d.label !== 'string' ||
        typeof d.color !== 'string'
    )
    if (bad) {
      console.warn('❌ Bad projectStatusChartData:', bad)
    }
  }, [projectStatusChartData])

  if (isFilteredTasksLoading) {
    return <DetailsLoading />
  }

  return (
    <Box sx={{ flexGrow: 1, padding: 2, pt: pinned ? '60px' : 0, transition: 'padding 0.3s ease' }}>
      <Grid container spacing={2}>
        {boards.map((board, index) => {
          return (
            <Grid item xs={12} sm={4} md={4} lg={4} xl={4} key={index}>
              <Card sx={{ height: '100%', overflow: 'visible' }}>
                <CardActionArea onClick={() => handleNavigate(board.route)} sx={{ height: '100%' }}>
                  <CardContent
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      height: '100%',
                    }}
                  >
                    {/* Board Title & Icon */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box>{board.icon}</Box>
                      <Typography variant="h5">{board.title}</Typography>
                    </Box>

                    {/* Show deal count if available */}
                    {board.count !== undefined && (
                      <Typography variant="h6" sx={{ marginTop: 1 }}>
                        {`${board.count} ${board.count === 1 ? 'Item' : 'Items'}`}
                      </Typography>
                    )}

                    {/* MUI Pie Chart for NCO statuses */}
                    {board.title === 'New Client Onboards' && pieChartData.length > 0 ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between', // Keep Key on Left & Chart on Right
                          alignItems: 'center',
                          width: '100%',
                          marginTop: 2,
                        }}
                      >
                        {/* 🔹 Legend (Left Side) */}
                        <Box sx={{ flex: 1, textAlign: 'left', minWidth: 150, paddingRight: '20px' }}>
                          {pieChartData.map(({ id, value }, index) => (
                            <Typography
                              key={`${id}-${value}-${index}`}
                              sx={{ color: renderColorByString(id), fontSize: 14 }}
                            >
                              {`${id}: ${value}`}
                            </Typography>
                          ))}
                        </Box>

                        {/* 🔹 Pie Chart (Right Side) */}
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                          <PieChart
                            series={[
                              {
                                data: pieChartData,
                                highlightScope: { fade: 'global', highlight: 'item' },
                                faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                                outerRadius: 90,
                                innerRadius: 40,
                              },
                            ]}
                            slotProps={{
                              legend: {
                                hidden: true,
                              },
                            }}
                            width={250}
                            height={250}
                            margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                          />
                        </Box>
                      </Box>
                    ) : board.title === 'Team Tasks' && pieChartData.length > 0 ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between', // Keep Key on Left & Chart on Right
                          alignItems: 'center',
                          width: '100%',
                          marginTop: 2,
                        }}
                      >
                        {/* 🔹 Legend (Left Side) */}
                        <Box sx={{ flex: 1, textAlign: 'left', minWidth: 150, paddingRight: '20px' }}>
                          {taskTypePieChartData.map(({ id, value }, index) => {
                            return (
                              <Typography
                                key={`${index}-${id}-${value}`}
                                sx={{ color: renderColorByString(id), fontSize: 14 }}
                              >
                                {`${id}: ${value}`}
                              </Typography>
                            )
                          })}
                        </Box>

                        {/* 🔹 Pie Chart (Right Side) */}
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                          <PieChart
                            series={[
                              {
                                data: taskTypePieChartData,
                                highlightScope: { fade: 'global', highlight: 'item' },
                                faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                                outerRadius: 90,
                                innerRadius: 40,
                              },
                            ]}
                            slotProps={{
                              legend: {
                                hidden: true,
                              },
                            }}
                            width={250}
                            height={250}
                            margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                          />
                        </Box>
                      </Box>
                    ) : board.title === 'Projects' && projectStatusChartData.length > 0 ? (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between', // Keep Key on Left & Chart on Right
                          alignItems: 'center',
                          width: '100%',
                          marginTop: 2,
                        }}
                      >
                        {/* 🔹 Legend (Left Side) */}
                        <Box sx={{ flex: 1, textAlign: 'left', minWidth: 150, paddingRight: '20px' }}>
                          {projectStatusChartData.map(({ id, value }, index) => {
                            return (
                              <Typography
                                key={`${index}-${id}-${value}`}
                                sx={{ color: renderColorByString(id), fontSize: 14 }}
                              >
                                {id}: {value}
                              </Typography>
                            )
                          })}
                        </Box>

                        {/* 🔹 Pie Chart (Right Side) */}
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                          <PieChart
                            series={[
                              {
                                data: projectStatusChartData,
                                highlightScope: { fade: 'global', highlight: 'item' },
                                faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                                outerRadius: 90,
                                innerRadius: 40,
                              },
                            ]}
                            slotProps={{
                              legend: {
                                hidden: true,
                              },
                            }}
                            width={250}
                            height={250}
                            margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                          />
                        </Box>
                      </Box>
                    ) : board.title === 'Monthly Task Board' && mtbStatusChartData.length > 0 ? (
                      <>
                        {Array.isArray(mtbStatusChartData) &&
                          mtbStatusChartData.length > 0 &&
                          mtbStatusChartData.every(d => d.id && typeof d.value === 'number') && (
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between', // Keep Key on Left & Chart on Right
                                alignItems: 'center',
                                width: '100%',
                                marginTop: 2,
                              }}
                            >
                              {/* 🔹 Legend (Left Side) */}
                              <Box sx={{ flex: 1, textAlign: 'left', minWidth: 150, paddingRight: '20px' }}>
                                {mtbStatusChartData.map(({ id, value }, index) => {
                                  const status = extractStatusFromId(id)

                                  return (
                                    <Typography
                                      key={`${index}-${id}-${value}`}
                                      sx={{ color: renderColorByString(status), fontSize: 14 }}
                                    >
                                      {status}: {value}
                                    </Typography>
                                  )
                                })}
                              </Box>

                              {/* 🔹 Pie Chart (Right Side) */}
                              <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
                                <PieChart
                                  series={[
                                    {
                                      data: mtbStatusChartData.map(({ id, value, label, color }) => ({
                                        id,
                                        value,
                                        label,
                                        color,
                                      })),
                                      highlightScope: { fade: 'global', highlight: 'item' },
                                      faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' },
                                      outerRadius: 90,
                                      innerRadius: 40,
                                    },
                                  ]}
                                  slotProps={{
                                    legend: { hidden: true },
                                  }}
                                  width={250}
                                  height={250}
                                  margin={{ top: 10, right: 10, bottom: 10, left: 10 }}
                                />
                              </Box>
                            </Box>
                          )}
                      </>
                    ) : null}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          )
        })}
      </Grid>
    </Box>
  )
}

export default Boards
